import axios from 'axios'
import { generateNewAccessToken, wait } from '../helpers/helpers'
import { getAccessToken } from './baseUrl'
import { IApiCaller, IAxiosConfig, IRetryPolicy, IStatusMessageError, IStringKeyValue } from '../interfaces/interfaces'
import store from 'redux-share/store'

const getLimit = () => 100

export const apiCaller: any = async ({ url, method = 'GET', data, headers, retryCount = 1, maxRetryCount = 0, errorMessage, retryPolicy }: IApiCaller) => {
  let axiosConfig: IAxiosConfig = {
    url: url,
    method: method,
    headers: {
      ...headers,
      'Content-Type': 'application/json',
    },
  }
  if (data) {
    if (method === 'GET') axiosConfig = { ...axiosConfig, params: {uuid: store.getState().videoData.params.uuid, provider: store.getState().videoData.params.provider === "unknown" ? undefined : store.getState().videoData.params.provider, ...data } }
    else axiosConfig = { ...axiosConfig, data: {uuid: store.getState().videoData.params.uuid, provider: store.getState().videoData.params.provider === "unknown" ? undefined : store.getState().videoData.params.provider, ...data } }
  } else {
    if (method === 'GET') axiosConfig = { ...axiosConfig, params: {uuid: store.getState().videoData.params.uuid, provider: store.getState().videoData.params.provider === "unknown" ? undefined : store.getState().videoData.params.provider } }
    else axiosConfig = { ...axiosConfig, data: {uuid: store.getState().videoData.params.uuid, provider: store.getState().videoData.params.provider === "unknown" ? undefined : store.getState().videoData.params.provider } }
  }
  try {
    return await axios(axiosConfig)
  } catch (error: any) {
    if (error && error.response && error.response.status === 401) {
      await generateNewAccessToken()
      const newHeader = {
        ...headers,
        authorization: `Bearer ${getAccessToken()}`,
      }
      let newRetryCount = retryCount + 1
      await wait(newRetryCount)
      return apiCaller({
        url,
        method,
        data,
        headers: newHeader,
        retryCount: newRetryCount,
        maxRetryCount,
        errorMessage,
        retryPolicy
      })

    } else {
        let newRetryDelay = 0, newMaxRetryCount = 0;
        if (retryPolicy !== undefined) {
          for (let i = 0; i < retryPolicy.length; i++) {
            if ((((retryPolicy[i].codeAbove && error.response.status >= retryPolicy[i].codeAbove!) || (retryPolicy[i].codeBelow && error.response.status <= retryPolicy[i].codeBelow!)) && (retryPolicy[i].excludeCodes.length === 0 || !retryPolicy[i].excludeCodes.includes(error.response.status))) || (retryPolicy[i].includeCodes.length === 0 || retryPolicy[i].includeCodes.includes(error.response.status))) {
              newMaxRetryCount = retryPolicy[i].count;
              newRetryDelay = retryPolicy[i].delay;
              break;
            }
           }
        } else {
          newMaxRetryCount = maxRetryCount;
          newRetryDelay = retryCount + 1;
        }
        if (retryCount < newMaxRetryCount) {
          await wait(newRetryDelay);
          return apiCaller({ url, method, data, headers, retryCount: retryCount + 1, newMaxRetryCount, errorMessage, retryPolicy });
        } else if(error.response) return { data: [], status: error.response.status };
    }
  }
}

export function get(event: (response: any, status: number) => void, url: string, params: { [k: string]: any } = {}, lang = 'fa', auth = true) {
  const generatedUrl = new URL(url);
  const newParams:any = {...params, uuid: store.getState().videoData.params.uuid, provider: store.getState().videoData.params.provider === "unknown" ? undefined : store.getState().videoData.params.provider }
  Object.keys(newParams).forEach((key) => {
    if (newParams[key] !== undefined) {
      generatedUrl.searchParams.append(key, newParams[key])
    }
  })
  let status: number
  fetch(generatedUrl.href, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  }).then(function(response) {
    status = response.status
    if (status === 429) {
      setTimeout(() => {
        get(event, url, params, lang, auth)
      }, getLimit())
    }
    return response.json()
  }).then(function(data) {
    event(data, status)
  }).catch((err) => {
    event(err, status)
  })
}

export function getAll(path: string, retryPolicy?: IRetryPolicy[], params?: any, retryCount: number = 1, maxRetryCount: number = 0) {
  return new Promise(async (resolve, reject) => {  // Added reject to the Promise executor function.
    try {
      const url = new URL(path);
      const limit = 1000;
      if (params) {
        Object.keys(params).forEach((key) => {
          if (params[key]) {
            url.searchParams.set(key, params[key]);
          }
        });
      }
      url.searchParams.set('limit', limit.toString());
      const response = await fetch(url.href);
      if (!response.ok) {  // Check if the response was not ok (status not in the range 200-299).
        throw response;
      }
      const data = await response.json();
      const count = data.count;
      const requestCount = Math.ceil(count / limit) - 1;
      const promises: Array<Promise<any>> = [];
      for (let i = 0; i < requestCount; i++) {
        const offset = i * limit;  // Fixed the offset calculation.
        url.searchParams.set('offset', offset.toString());
        const promise:Promise<any> = fetch(url.href)
          .then(res => {
            if (res.ok) {
              return res.json();
            }
            return res.json().then(err => Promise.reject(err));
          });
        promises.push(promise);
      }
      Promise.all(promises).then((res) => {
        const results = res.flatMap((item) => item.results);
        resolve([...data.results, ...results]);
      }).catch(reject);  // Added catch to handle any errors in the Promise.all.
    } catch (error:any) {
          let newRetryDelay = 0, newMaxRetryCount = 0;
          if (retryPolicy !== undefined) {
            for (let i = 0; i < retryPolicy.length; i++) {
              if (((retryPolicy[i].codeAbove && error.status >= retryPolicy[i].codeAbove!) || (retryPolicy[i].codeBelow && error.status <= retryPolicy[i].codeBelow!)) && (retryPolicy[i].excludeCodes.length === 0 || !retryPolicy[i].excludeCodes.includes(error.status)) && (retryPolicy[i].includeCodes.length === 0 || retryPolicy[i].includeCodes.includes(error.status))) {
                newMaxRetryCount = retryPolicy[i].count;
                newRetryDelay = retryPolicy[i].delay;
                break;
              }
            }
            if (retryCount < newMaxRetryCount) {
              await wait(newRetryDelay);
              getAll(path, retryPolicy, params, retryCount + 1, newMaxRetryCount)
            }
          }
    }
  });
}

class ApiCallError extends Error {
  status: number = 0
  retryQty: number = 0

  constructor(message?: string, status: number = 0, retryQty: number = 0) {
    super(message)
    this.status = status
    this.retryQty = retryQty
  }
}

const getProjectError: (errorKey: string) => IStatusMessageError = (errorKey: string) => {
  const projectErrors: { [key: string]: IStatusMessageError } = {
    timeout: {
      status: -1,
      message: 'timeout',
    },
    notFound: {
      status: 404,
      message: 'not found',
    },
    conflict: {
      status: 409,
      message: 'conflict',
    },
    frequentlyRequest: {
      status: 429,
      message: 'frequently request',
    },
    internalServerError: {
      status: 500,
      message: 'internal server error',
    },
    serviceUnavailable: {
      status: 503,
      message: 'service unavailable',
    },
  }
  return projectErrors[errorKey]
}

const getAnonValue: (key: string) => string = (key: string) => {
  return getAnonConfig()[key]
}

const anonConfigGenerator = (newAnonConfig: IStringKeyValue = {}) => {
  const anonConfig: IStringKeyValue = {
    ANON_FAILURE_RETRY_DELAY_THROTTLE: '5',
    ANON_FAILURE_RETRY_COUNT_THROTTLE: '3',
    FAILURE_RETRY_DELAY_INTERNAL_SERVER_ERROR: '5',
    FAILURE_RETRY_COUNT_INTERNAL_SERVER_ERROR: '3',
    FAILURE_RETRY_DELAY_TIMEOUT: '5',
    FAILURE_RETRY_COUNT_TIMEOUT: '3',
    LANG: 'fa',
    DIR: 'rtl',
  }
  const updateAnonConfig = { ...anonConfig, ...newAnonConfig }
  return () => updateAnonConfig
}

let getAnonConfig: () => IStringKeyValue = anonConfigGenerator()

export {
  getProjectError,
  ApiCallError,
  getAnonValue,
  getLimit,
}
